<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide, toRaw, nextTick} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Rating,
  Tag
} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Users from "@/components/Users.vue";
import UserProfile from "@/components/UserProfile/index.vue";
import HotOrderSkeleton from "@/views/orders/Details/hotOrders/HotOrderSkeleton.vue";
import DeliveredRating from "@/views/orders/Details/hotOrders/DeliveredRating.vue";
import CanceledRating from "@/views/orders/Details/hotOrders/CanceledRating.vue";
// import PublishHotOrder from "@/views/orders/Details/hotOrders/PublishHotOrder.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";

//stores
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore();

//loading flags
const {
  saving,
  fetching,
  loading,
  setSaving,
  setFetching,
  setLoading,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)


//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['refresh', 'refreshOrder']);

//props
const props = defineProps({
  addOrder: {
    type: Function,
    required: false
  },
  orderType: {
    type: String,
    required: true
  },
  order: {
    type: Object,
    required: false
  },
  selectedHotOrder: {
    type: Object,
    required: true
  },
  modalType: {
    type: String,
    default: 'order'
  }
})

//injects
const visible = inject('isShowUpdateHotOrderModal')

//component variables
const amount = ref(null)
const profitMargin = ref(null)
const selectedTaker = ref({})
const selectedCurrency = ref({})
const serverUsers = ref([])
const currencyPrices = ref([])
const activeTab = ref("0")
const hotOrderAmountNum = computed(() => Number(hotOrder.amount))
const amountNum = computed(() => Number(amount.value))
const rateNum = computed(() => Number(hotOrder.rate))
const valueNum = computed(() => Number(hotOrder.value))
const profitNum = computed(() => Number(profitMargin.value))
const isShowUserProfileModal = ref(false);
const isShowPublishModal = ref(false);
const isShowDeliveredRatingModal = ref(false);
const isShowCanceledRatingModal = ref(false);
const savingBeforePosting = ref(false)
const excludeCurrencyPrices = ["Toman Gold Buy Manual", "Toman Gold Buy Auto", "Toman Shop"];
const initialFinalized = ref(null);

const hasProfitField = computed(() => userStore.hasPermission('BIG_BOSS') && props.orderType === 'Account')
const canFinalizedOrder = computed(() => userStore.hasPermission('FINALIZE_ORDERS') || userStore.hasPermission('BIG_BOSS'))
const canShowUserProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))

const rating = 0
const hasRatingModal = computed(() => {
  if (props.orderType !== "Boost") return false

  const specialTakers = [
    {
      "id": 8,
      "name": "Dawn_Community"
    },
    {
      "id": 9,
      "name": "Oblivion_Community"
    },
    {
      "id": 11,
      "name": "Funpay_Community_5%"
    },
    {
      "id": 17,
      "name": "Eldorado_Community"
    },
    {
      "id": 71,
      "name": "Prestige_Community"
    },
    {
      "id": 74,
      "name": "Nightmare_Community"
    },
    {
      "id": 6970,
      "name": "Sylvanas_Community"
    },
    {
      "id": 7106,
      "name": "Direct_Payment"
    },
    {
      "id": 27,
      "name": "Matin_Fifa"
    },
    {
      "id": 6988,
      "name": "GenshinShop_Telegram"
    },
    {
      "id": 1513,
      "name": "jalal0098"
    }
  ]

  if (specialTakers.some(taker => taker.id === hotOrder.takerID) || hotOrder.rating) {
    return false
  }

  if (hotOrder.state === 'Delivered') return true
  return !!hasCanceledRatingModal.value;
})

// Funpay community check (by takerID === 11)
const isFunpayCommunity = computed(() => hotOrder.takerID === 11)

// NEW: Preview computations
const oldContribution = computed(() => props.selectedHotOrder.value);
const newContribution = computed(() => {
  return hotOrder.state !== 'Canceled' ? valueNum.value : 0;
});
const newTakersCut = computed(() => {
  return Number(props.order.takersCut || 0) + (newContribution.value - oldContribution.value);
});
const newRevenue = computed(() => {
  const income = Number(props.order.income || 0);
  return income - newTakersCut.value;
});

const isTakerIDRequired = computed(() => {
  return !selectedTaker.value?.id && hotOrder.state !== 'New' && hotOrder.state !== 'Canceled';
})

const hasCanceledRatingModal = computed(() => selectedTaker.value?.id && hotOrder.state === 'Canceled')

const states = ['New', 'Assigned', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Assigning: 'bg-[#EFECDC]',
  Assigned: 'bg-[#FFEB01]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const attachments = ref(null)
const hotOrder = reactive({
  "id": props.selectedHotOrder.id,
  "state": "",
  "title": "",
  "rate": "",
  "amount": "",
  "value": "",
  "currency": "",
  "takerID": null,
  "description": "",
  "notes": "",
  "rating": 0,
  "tags": [],
  "attachments": {
    "paymentReceipts": []
  },
  "finalized": null
})
const rules = computed(() => {
  return {
    state: {required},
    title: {required},
    rate: {required},
    amount: {required},
    value: {required},
    currency: {required},
    notes: hasCanceledRatingModal.value ? {required} : {},
    takerID: isTakerIDRequired.value ? {required} : {}
  };
});
const $vuelidate = useVuelidate(rules, hotOrder);
const sortedTags = computed(() => {
  if (!selectedTaker.value?.tags) return [];
  return Object.entries(selectedTaker.value.tags).sort((a, b) => b[1] - a[1]); // descending by value
});

//component functions
function formatNumber(num, decimals = 8) {
  return num.toFixed(decimals).replace(/\.?0+$/, '');
}

const onSave = async (isPublishing) => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    activeTab.value = "0"
    return false;
  }

  if (isUploadingAttachment.value) {
    activeTab.value = "1"
    return false;
  }

  if (attachments.value.length) {
    hotOrder.attachments = {paymentReceipts: attachments.value}
  }

  if (selectedTaker.value?.id && hotOrder.state === 'New') {
    showMessage('Order state must be updated to assign a taker.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
    return false
  }

  if (isTakerIDRequired.value) {
    console.log('it should have a taker')
  } else {
    if (hasRatingModal.value) {
      showRatingModal()
    } else {
      await saveHotOrder(isPublishing)
    }
  }
}

const saveHotOrder = async (isPublishing) => {
  if (isPublishing) {
    savingBeforePosting.value = true
  } else {
    setSaving(true)
  }
  try {
    const hasChangedFinalized = initialFinalized.value !== hotOrder.finalized

    const {finalized, ...rest} = hotOrder

    const payload = hasChangedFinalized
        ? {...rest, finalized}
        : {...rest}

    const cleanHotOrder = cleanObject(payload)

    const res = await ordersStore.action_updateHotOrder(props.orderType, cleanHotOrder)
    if (res.result === true) {
      //toast.add({severity: 'success', summary: 'Successful', detail: `Taker Updated!`, life: 1500});
      showMessage(`Taker Updated!`, 'success');
      if (!isPublishing) {
        emit('refreshOrder')
        visible.value = false
      }
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    if (isPublishing) {
      savingBeforePosting.value = false
    } else {
      setSaving(false)
    }
  }
}

function orderDetails(orderID) {
  const routeName = {
    Account: 'account-order-details',
    'Gift Card': 'gift-card-order-details',
    Item: 'item-order-details',
    Boost: 'boost-order-details'
  }
  const route = {name: routeName[props.orderType], params: {id: orderID}}
  router.replace(route)
}

const showRatingModal = () => {
  if (hotOrder.state === 'Delivered') {
    isShowDeliveredRatingModal.value = true;
  } else if (hasCanceledRatingModal.value) {
    isShowCanceledRatingModal.value = true;
  }
}

const onStateChange = () => {
  const isCanceled = hotOrder.state === 'Canceled';
  const isToman = hotOrder.currency === 'Toman';

  if (hasCanceledRatingModal.value) {
    hotOrder.value = "0"
    showMessage('Write the reason for canceling this taker.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
    return
  }

  if (isCanceled) {
    hotOrder.value = "0";
    return;
  }

  const amount = hotOrderAmountNum.value;
  const rate = rateNum.value;
  const value = isToman ? (amount * 1000) / rate : amount * rate;
  hotOrder.value = formatNumber(value, 2);
};
const onPriceInput = (e) => {
  hotOrder.rate = e.value
  if (hotOrder.state === 'Canceled') {
    hotOrder.value = "0"
    return
  }
  calc();
};
const onAmountInput = (e) => {
  amount.value = e.value
  if (hotOrder.state === 'Canceled') {
    hotOrder.value = "0"
    hotOrder.amount = amount.value
    return
  }
  calc()
};
const onProfitInput = () => {
  // profitMargin is used when we only have one hot order and is only used by BIG_BOSS.
  const profit = profitMargin.value || 0
  const priceWithProfit = Number(props.order.price * (profit / 100))
  const incomeNum = Number(props.order.income)
  hotOrder.amount = formatNumber(incomeNum - priceWithProfit, 2)
  amount.value = formatNumber(incomeNum - priceWithProfit, 2)
  hotOrder.value = formatNumber(hotOrderAmountNum.value * rateNum.value, 2)
}

const onChangeCurrency = (currency) => {
  hotOrder.currency = currency.name
  hotOrder.rate = currency.price
  calc();
}

const getSelectedTaker = (taker) => {
  if (selectedTaker.value?.id && hotOrder.state === 'New') {
    hotOrder.state = 'Assigned'
  }

  selectedTaker.value = taker || null
  hotOrder.takerID = taker ? taker.id : 0
  calc()
}

const getCurrencies = async (refresh) => {
  try {
    if (!refresh) setLoading(true)
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      currencyPrices.value = res.data;
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setLoading(false)
  }
}

const getUser = async () => {
  try {
    const res2 = await userStore.action_getCompleteProfile3({id: hotOrder.takerID})
    if (res2.result === true) {
      selectedTaker.value = res2.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res2, life: 1500});
      showMessage(res2, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  }
}

async function fetchInitialData(refresh = true) {
  try {
    if (!refresh) setFetching(true)
    await getCurrencies(false)
    const res = await ordersStore.action_getHotOrder(props.orderType, {id: props.selectedHotOrder.id})
    if (res.result === true) {
      Object.keys(hotOrder).forEach(key => {
        if (res.data.hasOwnProperty(key)) {
          hotOrder[key] = res.data[key]
        }
      })
      selectedCurrency.value = currencyPrices.value.find(item => item.name === hotOrder.currency)
      amount.value = hotOrder.amount
      initialFinalized.value = hotOrder.finalized
      if (hotOrder.takerID) {
        await getUser();
      }
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setFetching(false)
  }
}

const calc = () => {
  const currency = hotOrder.currency

  if (isFunpayCommunity.value) {
    hotOrder.amount = formatNumber(amountNum.value * 1.03, 2)
  } else {
    hotOrder.amount = amount.value
  }

  if (currency === 'Toman') {
    if (rateNum.value) {
      hotOrder.value = formatNumber((hotOrderAmountNum.value * 1000) / rateNum.value, 2)
    } else {
      hotOrder.value = "0"
    }
  } else {
    hotOrder.value = formatNumber(hotOrderAmountNum.value * rateNum.value, 2)
  }
}

function diffDeep(curr, base) {
  if (Object.is(curr, base)) return null

  if (
      typeof curr !== 'object' || curr === null ||
      typeof base !== 'object' || base === null
  ) {
    return curr                    // Some primitive or different type
  }

  const out = Array.isArray(curr) ? [] : {}
  let hasDiff = false
  const keys = Object.keys(curr)     // Only current keys
  keys.forEach(key => {
    const sub = diffDeep(curr[key], base[key])
    if (sub !== null) {
      out[key] = sub
      hasDiff = true
    }
  })

  return hasDiff ? out : null
}

function showUserProfileModal(user) {
  if (user.isLoading) return;
  if (canShowUserProfile.value) {
    userStore.setSelectedUser(user);
    isShowUserProfileModal.value = true
  }
}

const showPublishModal = async () => {
  // console.log(props.selectedHotOrder)
  // console.log(hotOrder)

  const fieldsToCompare = [
    'state',
    'title',
    'currency',
    'description',
    'notes'
  ]

  const numberFields = ['rate', 'amount', 'value']

  let hasChanges = false
  let changedFields = []

  for (const field of fieldsToCompare) {
    if (hotOrder[field] !== props.selectedHotOrder[field]) {
      changedFields.push(field)
      hasChanges = true
    }
  }

  for (const field of numberFields) {
    if (Number(hotOrder[field]) !== Number(props.selectedHotOrder[field])) {
      changedFields.push(field)
      hasChanges = true
    }
  }

  if (hotOrder.takerID && hotOrder.takerID !== props.selectedHotOrder.takerID) {
    changedFields.push('takerID')
    hasChanges = true
  }

  if (attachments.value.length) {
    changedFields.push('attachments')
    hasChanges = true
  }

  //console.log('hasChanges:', hasChanges)
  //console.log('changedFields:', changedFields)

  if (hasChanges) {
    const ok = await showAlert('Some hotOrder fields have changed', 'Do you want to save?', 'Yes', 'No');
    if (ok) {
      await onSave(false)
    } else {
      for (const field of changedFields) {
        hotOrder[field] = props.selectedHotOrder[field]
      }
      await nextTick()
      isShowPublishModal.value = true
    }
  } else {
    isShowPublishModal.value = true
  }
}

const changeFinalized = () => {
  if (hotOrder.finalized) {
    hotOrder.finalized = false
  } else {
    hotOrder.finalized = true
  }
}

//hooks
onMounted(() => {
  fetchInitialData(false)
})

//provides
provide('isShowUserProfileModal', isShowUserProfileModal)
provide('isShowDeliveredRatingModal', isShowDeliveredRatingModal)
provide('isShowCanceledRatingModal', isShowCanceledRatingModal)
provide('isShowPublishModal', isShowPublishModal)
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)] !px-3">
    <template #header>
      <div class="text-base py-3">
        Update Sub Order
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 px-2 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <HotOrderSkeleton v-if="fetching" :orderType="orderType"/>
      <template v-else>
        <Tabs v-model:value="activeTab" class="custom-tabs">
          <div class="flex flex-row justify-between items-center border-b border-slate-700 pb-2">
            <TabList>
              <Tab value="0">Details</Tab>
              <Tab value="1">Attachments</Tab>
            </TabList>
            <div class="flex flex-row gap-2">
              <div v-if="canFinalizedOrder" @click="changeFinalized"
                   class="flex gap-2 items-center rounded-md h-[28px] 2xl:h-[32px] text-xs px-2 py-1 border cursor-pointer select-none"
                   :class="[hotOrder.finalized ? 'bg-primary-700 border-transparent' : 'bg-transparent border-slate-700']">
                <div class="flex items-center size-3.5 border rounded-sm"
                     :class="[hotOrder.finalized ? 'border-slate-400' : 'border-slate-700']">
                  <svg v-if="hotOrder.finalized" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-check-icon lucide-check">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                </div>
                Finalized
              </div>
              <div class="flex flex-col gap-0.5">
                <FloatLabel class="w-full custom-select"
                            :class="{ 'has-error': $vuelidate.state.$error}"
                            variant="on">
                  <Select v-model="hotOrder.state" inputId="state" :options="states"
                          class="w-full min-w-28 !bg-transparent h-[28px] 2xl:h-[32px] !text-xs 2xl:!text-sm"
                          :class="[$vuelidate.state.$error ? '!border !border-red-300' : '!border-slate-700']"
                          input-class="!py-1 !items-center !flex !pr-0"
                          overlay-class="select-overlay-in-modal h-max"
                          @change="onStateChange">
                    <template #value="slotProps">
                      <div class="flex items-center gap-2 !text-xs">
                        <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.value]"></span>
                        {{ slotProps.value }}
                      </div>
                    </template>
                    <template #option="slotProps">
                      <div class="flex items-center gap-2 !text-xs 2xl:!text-sm">
                        <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.option]"></span>
                        {{ slotProps.option }}
                      </div>
                    </template>
                  </Select>
                </FloatLabel>
                <template v-if="$vuelidate.state.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.state.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
            </div>
          </div>
          <TabPanels>
            <TabPanel value="0">
              <div class="grid grid-cols-12 gap-4">
                <!--                <div v-if="hotOrder.tags.length > 0" class="col-span-12 flex flex-wrap gap-2">
                                  <Tag v-for="tag in hotOrder?.tags" :severity="configStore.tags[tag] === 1 ? 'success' : 'danger'" :value="tag"
                                       class="!font-normal !text-xs"></Tag>
                                </div>-->
                <div v-if="modalType === 'order'" class="col-span-12">
                  <div class="bg-slate-800 rounded-md p-3 border border-slate-700">
                    <h4 class="text-sm font-medium text-slate-300 mb-2">Preview Changes</h4>
                    <div class="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p class="text-slate-400">New Takers Cut</p>
                        <p class="text-white font-mono">${{ formatPrice(formatNumber(newTakersCut, 2)) }}</p>
                      </div>
                      <div>
                        <p class="text-slate-400">New Revenue</p>
                        <p class="text-white font-mono">${{ formatPrice(formatNumber(newRevenue, 2)) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-span-8 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                  <FloatLabel variant="on">
                    <InputText id="hotOrderTitle" v-model="hotOrder.title" autocomplete="off"
                               class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                               :class="{ 'has-error': $vuelidate.title.$error }"
                               :disabled="modalType === 'subOrder'"/>
                    <label for="hotOrderTitle" class="!bg-slate-900"
                           :class="{ 'focus:text-red-300': $vuelidate.title.$error }">Title</label>
                  </FloatLabel>
                  <template v-if="$vuelidate.title.$error">
                    <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                       v-if="!$vuelidate.title.required.$response">
                      This field is required
                    </p>
                  </template>
                </div>
                <div class="col-span-4 flex flex-row gap-2 text-xs 2xl:text-sm">
                  <div class="flex-1 w-full">
                    <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.currency.$error }">
                      <Select v-model="selectedCurrency" input-id="currency"
                              :options="currencyPrices.filter(item => !excludeCurrencyPrices.includes(item.name))"
                              optionLabel="name" :loading="loading"
                              class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                              :class="{ 'has-error has-addon': $vuelidate.currency.$error }"
                              label-class="!text-sm !items-center !flex"
                              @update:modelValue="onChangeCurrency"
                              :disabled="modalType === 'subOrder'">
                        <template #option="slotProps">
                          <div class="flex flex-row items-center gap-4 justify-between w-full">
                            <span class="text-sm">{{ slotProps.option.name }}</span>
                            <div class="flex flex-row gap-2 items-center">
                              <span class="text-xs">
                                {{ slotProps.option.price }}{{ slotProps.option.name === 'Toman' ? 'Tmn' : '$' }}
                              </span>
                              <div class="h-4 w-[1px] bg-slate-500"></div>
                              <span class="text-[.7rem]">
                                {{ gregorianDate(slotProps.option.lastUpdate, 'DD MMM HH:mm') }}
                              </span>
                            </div>
                          </div>
                        </template>
                        <template #value="slotProps">
                          <p :class="{'text-slate-400':modalType === 'subOrder'}">
                            {{ slotProps.value.name ? slotProps.value.name : '' }}</p>
                        </template>
                      </Select>
                      <label for="currency" class="!bg-slate-900"
                             :class="{ 'has-error': $vuelidate.currency.$error }">Currency</label>
                    </FloatLabel>
                  </div>
                  <button :disabled="modalType === 'subOrder'" @click="getCurrencies(false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-refresh-cw-icon lucide-refresh-cw">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                      <path d="M21 3v5h-5"/>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                      <path d="M8 16H3v5"/>
                    </svg>
                  </button>
                </div>
                <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm"
                     :class="[hasProfitField ? 'col-span-3' : 'col-span-4']">
                  <InputGroup>
                    <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.amount.$error }">
                      <InputNumber id="amount" v-model="amount"
                                   autocomplete="off" :maxFractionDigits="2"
                                   :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700',
                                     { 'has-error has-addon': $vuelidate.amount.$error }
                                   ]"
                                   @input="onAmountInput"
                                   :disabled="modalType === 'subOrder'"/>
                      <label for="amount" class="!bg-slate-900"
                             :class="{ 'has-error': $vuelidate.amount.$error }">Amount</label>
                    </FloatLabel>
                    <InputGroupAddon
                        class="!bg-transparent !border-slate-700 h-full text-[.7rem] whitespace-nowrap w-auto flex-shrink-0 !px-1">
                      {{ hotOrder.currency === 'Toman' ? '1000 Tmn' : hotOrder.currency }}
                    </InputGroupAddon>
                  </InputGroup>
                  <!-- Effective Amount display for funpay -->
                  <p v-if="isFunpayCommunity && hotOrder.amount && amount && hotOrder.state !== 'Canceled'"
                     class="text-yellow-400 text-xs mt-1">
                    Effective Amount: ${{ hotOrder.amount }}
                  </p>
                  <template v-if="$vuelidate.amount.$error">
                    <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                       v-if="!$vuelidate.amount.required.$response">
                      This field is required
                    </p>
                  </template>
                </div>
                <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm"
                     :class="[hasProfitField ? 'col-span-3' : 'col-span-4']">
                  <InputGroup>
                    <FloatLabel variant="on"
                                :class="{ 'has-error': $vuelidate.rate.$error }">
                      <InputNumber id="hotOrderPrice" v-model="hotOrder.rate"
                                   autocomplete="off" :max-fraction-digits="8"
                                   input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :input-class="{ 'has-error has-addon': $vuelidate.rate.$error }"
                                   @input="onPriceInput"
                                   :disabled="modalType === 'subOrder'"/>
                      <label for="hotOrderPrice" class="!bg-slate-900"
                             :class="{ 'has-error': $vuelidate.rate.$error }">Rate</label>
                    </FloatLabel>
                    <InputGroupAddon class="!bg-transparent !border-slate-700 h-full text-xs">
                      {{ hotOrder.currency === 'Toman' ? 'Tmn' : '$' }}
                    </InputGroupAddon>
                  </InputGroup>
                  <template v-if="$vuelidate.rate.$error">
                    <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                       v-if="!$vuelidate.rate.required.$response">
                      This field is required
                    </p>
                  </template>
                </div>
                <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm"
                     :class="[hasProfitField ? 'col-span-3' : 'col-span-4']">
                  <InputGroup>
                    <FloatLabel variant="on"
                                :class="{ 'has-error': $vuelidate.value.$error }">
                      <InputNumber id="value" v-model="hotOrder.value"
                                   autocomplete="off" readonly disabled :maxFractionDigits="2"
                                   input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :input-class="{ 'has-error has-addon': $vuelidate.value.$error, 'p-filled' : hotOrder.value }"
                                   :disabled="modalType === 'subOrder'"/>
                      <label for="value" class="!bg-slate-900"
                             :class="{ 'has-error': $vuelidate.value.$error }">Value</label>
                    </FloatLabel>
                    <InputGroupAddon class="!bg-transparent !border-slate-700 h-full text-xs">$
                    </InputGroupAddon>
                  </InputGroup>
                  <template v-if="$vuelidate.value.$error">
                    <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                       v-if="!$vuelidate.value.required.$response">
                      This field is required
                    </p>
                  </template>
                </div>
                <div v-if="hasProfitField"
                     class="col-span-3 flex flex-col gap-0.5">
                  <InputGroup>
                    <FloatLabel variant="on">
                      <InputText v-allow-digits-dot id="profitMargin" v-model="profitMargin"
                                 autocomplete="off"
                                 class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                 @input="onProfitInput"
                                 :disabled="modalType === 'subOrder'"/>
                      <label for="profitMargin" class="!bg-slate-900">Profit Margin</label>
                    </FloatLabel>
                    <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">%</InputGroupAddon>
                  </InputGroup>
                </div>
                <div class="col-span-12 flex flex-row gap-2 text-xs 2xl:text-sm">
                  <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                    <Users v-model="selectedTaker" @update:modelValue="getSelectedTaker" appendTo="body" label="Taker"
                           :required="isTakerIDRequired" :disabled="modalType === 'subOrder'"/>
                  </div>
                </div>
                <div v-if="selectedTaker?.id"
                     class="col-span-12 flex flex-col w-full gap-2 border border-slate-700 rounded-md p-2">
                  <div class="flex flex-row gap-2 w-full items-center">
                    <div @click.stop="showUserProfileModal(selectedTaker)"
                         :class="[canShowUserProfile ? 'cursor-zoom-in' : 'cursor-default']">
                      <img v-if="selectedTaker.discordAvatar" :src="selectedTaker.discordAvatar"
                           alt="avatar" class="size-10 rounded-full border border-slate-700">
                      <div v-else
                           class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                             stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 grid grid-cols-2 gap-1">
                      <div class="flex flex-col">
                        <div class="flex flex-row gap-2 items-center">
                          <div>{{ selectedTaker.name }} &nbsp &nbsp</div>
                          <div v-if="selectedTaker.rating" class="flex flex-row items-center gap-0.5 text-xs">
                          <span class="flex flex-row items-end">
                            {{ selectedTaker.rating }}⭐ ({{ selectedTaker.ratingCount }})
                          </span>
                          </div>
                        </div>
                        <div class="flex flex-row w-max text-xs text-slate-400">
                          <div>{{ selectedTaker.discordUsername ?? "-" }}</div>
                          <div>{{ "&nbsp- " + selectedTaker.discordDisplayName ?? "-" }}</div>
                        </div>
                      </div>
                      <div class="flex flex-col">
                        <div>{{ selectedTaker.verificationState ?? "-" }}</div>
                        <div>{{ discordRegistrationTime(selectedTaker.discordID) }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-if="selectedTaker?.tags && Object.entries(selectedTaker.tags).length > 0"
                       class="flex flex-wrap gap-1">
                    <Tag v-for="[key, value] in sortedTags"
                         :severity="configStore.tags[key] === 1 ? 'success' : 'danger'"
                         :value="`${key} (${value})`" class="!font-normal !text-xs"></Tag>
                  </div>
                </div>
                <div class="col-span-12 grid grid-cols-2 gap-4">
                  <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <FloatLabel variant="on">
                      <Textarea v-auto-dir id="description" v-model="hotOrder.description" rows="5"
                                style="resize: none" :disabled="modalType === 'subOrder'"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
                      <label for="description" class="!bg-slate-900">Description</label>
                    </FloatLabel>
                  </div>
                  <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                    <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.notes.$error }">
                      <Textarea v-auto-dir id="notes" v-model="hotOrder.notes" rows="5" style="resize: none"
                                :class="['!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700',
                                     { 'has-error': $vuelidate.notes.$error }
                                   ]" :disabled="modalType === 'subOrder'"/>
                      <label for="notes" class="!bg-slate-900">Notes</label>
                    </FloatLabel>
                  </div>
                </div>
              </div>
            </TabPanel>
            <TabPanel value="1">
              <Attachments
                  id="hotOrder"
                  :count="16"
                  :backend-images="hotOrder.attachments?.paymentReceipts ?? []"
                  :only-changes="true"
                  size-class="w-24 h-24"
                  font-size="text-[.6rem]"
                  v-model:uploading="isUploadingAttachment"
                  @update:payload="val => (attachments = val)"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </template>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="onSave(false)"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving || loading">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
  <UserProfile v-if="isShowUserProfileModal" :userID="userStore.selectedUser.id"/>
  <DeliveredRating v-if="isShowDeliveredRatingModal" @save="saveHotOrder"
                   :orderType="props.orderType" :hotorder="hotOrder"/>
  <CanceledRating v-if="isShowCanceledRatingModal" @save="saveHotOrder"
                  :orderType="props.orderType" :hotorder="hotOrder"
                  @skipRating="saveHotOrder"/>
</template>
<style>
.swal2-container.swal2-center.swal2-backdrop-show {
  z-index: 100000;
}
</style>